(function () {
  const CDN = "https://cdn.shopify.com/s/files/1/0638/1725/3114/files/";

  // ── SHOPIFY LAYER VARIANTS ─────────────────────────────────────────────────
  // The section emits one flat entry per layer product; this builds the lookup the
  // configurator actually queries: scent -> size -> layer -> { id, price, available }.
  // Prices come from Shopify rather than a table in here, so a price change in admin
  // is live immediately and the configurator can never quote a stale figure.
  const VARIANTS = (function () {
    const map = {};
    let entries = [];
    try {
      const dataEl = document.getElementById("col-variant-data");
      entries = dataEl ? JSON.parse(dataEl.textContent) : [];
    } catch (err) {
      console.error(
        "[Circle of Light] Could not parse layer variant data:",
        err,
      );
    }
    entries.forEach((entry) => {
      map[entry.scent] = map[entry.scent] || {};
      map[entry.scent][entry.size] = map[entry.scent][entry.size] || {};
      map[entry.scent][entry.size][entry.layer] = entry;
    });

    // An empty map and a mis-tagged product fail identically from the shopper's side, so say
    // which it is here. Empty almost always means the section's collection settings are unset,
    // and without this the per-combination warning sends you hunting for metafields instead.
    if (entries.length === 0) {
      console.warn(
        "[Circle of Light] No layer products loaded — nothing can be added to the " +
          "cart. The report below says which of the three causes it is: the collection not " +
          "resolving, resolving but empty, or holding products whose metafields are not set.",
      );

      // Dumped rather than summarised on purpose: "metafieldKeys" lists the namespaced keys each
      // product actually has, which is the fastest way to spot a namespace or spelling mismatch.
      try {
        const diagEl = document.getElementById("col-variant-diagnostics");
        if (diagEl)
          console.warn(
            "[Circle of Light] Layer collections:",
            JSON.parse(diagEl.textContent),
          );
      } catch (err) {
        console.error("[Circle of Light] Could not parse diagnostics:", err);
      }
    } else {
      const sizes = {};
      entries.forEach((entry) => {
        sizes[entry.size] = (sizes[entry.size] || 0) + 1;
      });
      ["large", "medium", "small"].forEach((size) => {
        const expected = size === "small" ? 20 : 30;
        if (!sizes[size]) {
          console.warn(
            "[Circle of Light] No " +
              size +
              " layer products loaded — check the " +
              size +
              " collection is set on the section and its products have their metafields.",
          );
        } else if (sizes[size] < expected) {
          console.warn(
            "[Circle of Light] Only " +
              sizes[size] +
              " of " +
              expected +
              " " +
              size +
              " layers loaded. Products missing custom.candle_scent or custom.candle_layer are " +
              "skipped, and a collection over 50 products is truncated by Liquid.",
          );
        }
      });
    }

    return map;
  })();

  // Small is a two-part sphere; medium and large take a middle section too.
  function layersFor(size) {
    return size === "small" ? ["top", "bottom"] : ["top", "mid", "bottom"];
  }

  // Resolves the candle on screen to its layer variants. A null variant means that
  // scent/size/layer is missing from the collection or missing its metafields, which
  // is the one failure the shopper must never be allowed to reach the cart with.
  function currentLayers() {
    return layersFor(state.size).map((layer) => {
      const scent =
        layer === "top"
          ? state.top
          : layer === "mid"
            ? state.mid
            : state.bottom;
      const byScent = VARIANTS[scent] || {};
      const bySize = byScent[state.size] || {};
      return { layer: layer, scent: scent, variant: bySize[layer] || null };
    });
  }

  function candleTotal() {
    const layers = currentLayers();
    if (layers.some((l) => !l.variant)) return null;
    return layers.reduce((sum, l) => sum + l.variant.price, 0);
  }

  // Intl rather than a hard-coded symbol table: Shopify Markets sets cart.currency,
  // so the configurator follows whatever currency the shopper is actually buying in.
  const MONEY = new Intl.NumberFormat(document.documentElement.lang || "en", {
    style: "currency",
    currency: (window.COL_CONFIG && window.COL_CONFIG.currency) || "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  function formatMoney(cents) {
    return MONEY.format(cents / 100);
  }

  function formatTotal() {
    const total = candleTotal();
    return total === null ? "—" : formatMoney(total);
  }

  function generateBundleId() {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return crypto.randomUUID();
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      },
    );
  }

  const SWATCH_COLORS = {
    light: "#d08e32",
    water: "#5c848e",
    bloom: "#5c4c87",
    endless: "#b82b35",
    psyche: "#5a2540",
    reflections: "#353d75",
    feathers: "#67ac33",
    python: "#907e71",
    fur: "#948c84",
    gold: "#ceb04e",
  };

  const SCENT_NOTES = {
    light: {
      mood: "Warm, sunny, optimistic",
      top: "Bergamot, lemon, neroli",
      heart: "Grapefruit, petitgrain",
      base: "Fig, white musk",
    },
    water: {
      mood: "Fresh, bright, Mediterranean",
      top: "Lemon, bergamot",
      heart: "Orange, grapefruit",
      base: "Norlimbanol, soft musk",
    },
    bloom: {
      mood: "Dewy, floral, luminous",
      top: "Freesia accord",
      heart: "Moss, cashmeran, hedione",
      base: "Musk, vanilla",
    },
    endless: {
      mood: "Passionate, juicy, bold",
      top: "Blackcurrant, raspberry, calone",
      heart: "Hedione, intense wood",
      base: "Musk, sandalwood",
    },
    reflections: {
      mood: "Green, calming, grounding",
      top: "Verbena, bergamot",
      heart: "Lemongrass, hedione",
      base: "Musk, patchouli, habanolide",
    },
    psyche: {
      mood: "Floral, tender, sensual",
      top: "Petitgrain, bergamot",
      heart: "Rose, peony, hedione, geranium",
      base: "Musk, styrax, vanilla",
    },
    feathers: {
      mood: "Serene, meditative, airy",
      top: "Calone, bergamot",
      heart: "White tea, neroli, guaiac wood, moss",
      base: "Musk, sandalwood",
    },
    python: {
      mood: "Ethereal, poetic, dreamy",
      top: "Wisteria accord",
      heart: "Gardenia, norlimbanol woods",
      base: "Ylang-ylang, vanilla",
    },
    fur: {
      mood: "Pure, luminous, refined",
      top: "Rose, white accords, bergamot, blackcurrant",
      heart: "Hedione",
      base: "Musk, romandolide",
    },
    gold: {
      mood: "Delicate, powerful, dawn-like",
      top: "Peony accord, cetalox",
      heart: "Hedione, cassione",
      base: "Musk, patchouli",
    },
  };

  let state = {
    size: "medium",
    position: "top",
    top: "light",
    mid: "light",
    bottom: "light",
  };

  const imgShadow = document.getElementById("img-shadow");
  const imgBottom = document.getElementById("img-bottom");
  const imgMid = document.getElementById("img-mid");
  const imgTop = document.getElementById("img-top");
  const sizeBadge = document.getElementById("col-size-badge");
  const priceEl = document.getElementById("col-price");
  const btnMid = document.getElementById("btn-mid");
  const sumMidRow = document.getElementById("sum-mid-row");
  const fsMidRow = document.getElementById("fs-mid-row");

  // ── ZONE MAP ───────────────────────────────────────────────────────────────
  const zoneCanvas = document.createElement("canvas");
  const zoneCtx = zoneCanvas.getContext("2d");
  const zoneCanvasSmall = document.createElement("canvas");
  const zoneCtxSmall = zoneCanvasSmall.getContext("2d");

  function loadZoneMap(img, canvas, ctx, url) {
    img.crossOrigin = "anonymous";
    img.onload = () => {
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      ctx.drawImage(img, 0, 0);
    };
    img.src = url;
  }

  const zoneImg = new Image();
  const zoneImgSmall = new Image();
  loadZoneMap(zoneImg, zoneCanvas, zoneCtx, CDN + "col-zone-map.png");
  loadZoneMap(
    zoneImgSmall,
    zoneCanvasSmall,
    zoneCtxSmall,
    CDN + "col-zone-map-small.png",
  );

  function sampleZone(e, canvasEl) {
    const rect = canvasEl.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;
    const isSmall = state.size === "small";
    const activeCtx = isSmall ? zoneCtxSmall : zoneCtx;
    const activeMap = isSmall ? zoneCanvasSmall : zoneCanvas;
    if (activeMap.width === 0) return null;
    const mapX = Math.floor((clickX / rect.width) * activeMap.width);
    const mapY = Math.floor((clickY / rect.height) * activeMap.height);
    let pixel;
    try {
      pixel = activeCtx.getImageData(mapX, mapY, 1, 1).data;
    } catch (err) {
      return null;
    }
    const r = pixel[0],
      g = pixel[1],
      b = pixel[2],
      a = pixel[3];
    if (a < 50) return null;
    if (r > 200 && g > 200 && b > 200) return null;
    if (r > 150 && g < 100 && b < 100) return "top";
    if (b > 150 && r < 100 && g < 100) return "mid";
    if (g > 150 && r < 100 && b < 100) return "bottom";
    return null;
  }

  const canvasEl = document.getElementById("col-canvas");
  const cursorEl = document.getElementById("col-cursor");
  const cursorLabel = document.getElementById("col-cursor-label");
  const cursorCircle = document.getElementById("col-cursor-circle");
  const ZONE_LABELS = {
    top: "Select Top",
    mid: "Select Middle",
    bottom: "Select Bottom",
  };

  canvasEl.addEventListener("contextmenu", function (e) {
    e.preventDefault();
  });

  canvasEl.addEventListener("click", function (e) {
    const zone = sampleZone(e, this);
    if (!zone) return;
    if (zone === "mid" && state.size === "small") return;
    state.position = zone;
    setPosBtnActive(zone);
    updateSwatchActive();
  });

  canvasEl.addEventListener("mousemove", function (e) {
    cursorEl.style.display = "flex";
    cursorEl.style.left = e.clientX + "px";
    cursorEl.style.top = e.clientY + "px";
    const zone = sampleZone(e, this);
    if (zone && !(zone === "mid" && state.size === "small")) {
      cursorLabel.textContent = ZONE_LABELS[zone];
      const zoneCollection =
        zone === "top" ? state.top : zone === "mid" ? state.mid : state.bottom;
      cursorCircle.style.background =
        SWATCH_COLORS[zoneCollection] || "#3a2419";
      cursorEl.style.opacity = "1";
    } else {
      cursorEl.style.opacity = "0";
    }
  });

  canvasEl.addEventListener("mouseleave", function () {
    cursorEl.style.display = "none";
  });
  // ── END ZONE MAP ───────────────────────────────────────────────────────────

  function imgUrl(collection, layer, isSmall) {
    return `${CDN}${collection}-${layer}${isSmall ? "-small" : ""}.png`;
  }

  function capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  function updateCanvas() {
    const sm = state.size === "small";
    const canvas = document.getElementById("col-canvas");

    // Update data-size so CSS media queries pick the right fixed size
    canvas.dataset.size = state.size;
    document.querySelector(".col-stage").dataset.size = state.size;

    // JS sets the default size; CSS media queries override at smaller screens
    const canvasSize =
      state.size === "large"
        ? "800px"
        : state.size === "medium"
          ? "600px"
          : "560px";
    canvas.style.width = canvasSize;
    canvas.style.height = canvasSize;

    imgShadow.src = `${CDN}bottom-shadow${sm ? "-small" : ""}.png`;
    imgBottom.src = imgUrl(state.bottom, "bottom", sm);
    imgTop.src = imgUrl(state.top, "top", sm);

    if (sm) {
      imgMid.src = "";
      imgMid.classList.add("hidden");
    } else {
      imgMid.src = imgUrl(state.mid, "mid", false);
      imgMid.classList.remove("hidden");
    }

    sizeBadge.textContent = capitalize(state.size);
    const totalText = formatTotal();
    priceEl.textContent = totalText;
    const mobilePriceEl = document.getElementById("col-price-mobile");
    if (mobilePriceEl) mobilePriceEl.textContent = totalText;
  }

  function setNotes(prefix, collection) {
    const n = SCENT_NOTES[collection] || {};
    const el = (id) => document.getElementById(id);
    if (el(prefix + "-mood")) el(prefix + "-mood").textContent = n.mood || "";
    if (el(prefix + "-top-note"))
      el(prefix + "-top-note").textContent = n.top ? "Top: " + n.top : "";
    if (el(prefix + "-heart-note"))
      el(prefix + "-heart-note").textContent = n.heart
        ? "Heart: " + n.heart
        : "";
    if (el(prefix + "-base-note"))
      el(prefix + "-base-note").textContent = n.base ? "Base: " + n.base : "";
  }

  function updateSummary() {
    const showMid = state.size !== "small";
    const { top, mid, bottom } = state;
    const el = (id) => document.getElementById(id);

    // Reset all rows visible, then selectively hide duplicates
    el("sum-bottom-row").style.display = "";
    sumMidRow.style.display = showMid ? "" : "none";

    if (showMid) {
      if (top === mid && mid === bottom) {
        el("sum-top-key").textContent = "All Layers";
        el("sum-top").textContent = capitalize(top);
        setNotes("sum-top", top);
        sumMidRow.style.display = "none";
        el("sum-bottom-row").style.display = "none";
      } else if (top === mid) {
        el("sum-top-key").textContent = "Top & Middle";
        el("sum-top").textContent = capitalize(top);
        setNotes("sum-top", top);
        sumMidRow.style.display = "none";
        el("sum-bottom-key").textContent = "Bottom";
        el("sum-bottom").textContent = capitalize(bottom);
        setNotes("sum-bottom", bottom);
      } else if (mid === bottom) {
        el("sum-top-key").textContent = "Top";
        el("sum-top").textContent = capitalize(top);
        setNotes("sum-top", top);
        el("sum-mid-key").textContent = "Middle & Bottom";
        el("sum-mid").textContent = capitalize(mid);
        setNotes("sum-mid", mid);
        el("sum-bottom-row").style.display = "none";
      } else {
        el("sum-top-key").textContent = "Top";
        el("sum-top").textContent = capitalize(top);
        setNotes("sum-top", top);
        el("sum-mid-key").textContent = "Middle";
        el("sum-mid").textContent = capitalize(mid);
        setNotes("sum-mid", mid);
        el("sum-bottom-key").textContent = "Bottom";
        el("sum-bottom").textContent = capitalize(bottom);
        setNotes("sum-bottom", bottom);
      }
    } else {
      // Small candle — 2 layers
      if (top === bottom) {
        el("sum-top-key").textContent = "Top & Bottom";
        el("sum-top").textContent = capitalize(top);
        setNotes("sum-top", top);
        el("sum-bottom-row").style.display = "none";
      } else {
        el("sum-top-key").textContent = "Top";
        el("sum-top").textContent = capitalize(top);
        setNotes("sum-top", top);
        el("sum-bottom-key").textContent = "Bottom";
        el("sum-bottom").textContent = capitalize(bottom);
        setNotes("sum-bottom", bottom);
      }
    }

    document.getElementById("fs-size").textContent = capitalize(state.size);
    document.getElementById("fs-top").textContent = capitalize(state.top);
    document.getElementById("fs-mid").textContent = capitalize(state.mid);
    document.getElementById("fs-bottom").textContent = capitalize(state.bottom);
    fsMidRow.style.display = showMid ? "" : "none";

    const fsTotal = document.getElementById("fs-total");
    if (fsTotal) fsTotal.textContent = formatTotal();
  }

  function updateSwatchActive() {
    const current =
      state.position === "mid"
        ? state.mid
        : state.position === "top"
          ? state.top
          : state.bottom;
    document.querySelectorAll(".col-swatch-item").forEach((el) => {
      el.classList.toggle("active", el.dataset.collection === current);
    });
  }

  function setPosBtnActive(pos) {
    document.querySelectorAll(".col-pos-btn").forEach((b) => {
      b.classList.remove("active");
      b.style.color = "";
    });
    const target = document.querySelector(`.col-pos-btn[data-pos="${pos}"]`);
    if (target) {
      target.classList.add("active");
      target.style.color = "#FFFFFF";
    }
  }

  function refresh() {
    updateCanvas();
    updateSummary();
    updateSwatchActive();
  }

  document.querySelectorAll(".col-pill").forEach((btn) => {
    btn.addEventListener("click", () => {
      state.size = btn.dataset.size;
      document
        .querySelectorAll(".col-pill")
        .forEach((b) => b.classList.remove("active"));
      btn.classList.add("active");
      if (state.size === "small") {
        btnMid.disabled = true;
        if (state.position === "mid") {
          state.position = "top";
          setPosBtnActive("top");
        }
      } else {
        btnMid.disabled = false;
      }
      refresh();
    });
  });

  document.querySelectorAll(".col-pos-btn").forEach((btn) => {
    btn.addEventListener("click", () => {
      if (btn.disabled) return;
      state.position = btn.dataset.pos;
      setPosBtnActive(btn.dataset.pos);
      updateSwatchActive();
    });
  });

  // Inject mood text into swatches for hover display
  document.querySelectorAll(".col-swatch-item").forEach((el) => {
    const mood = (SCENT_NOTES[el.dataset.collection] || {}).mood;
    if (mood) {
      const moodEl = document.createElement("span");
      moodEl.className = "col-swatch-mood";
      moodEl.textContent = mood;
      el.appendChild(moodEl);
    }
  });

  document.querySelectorAll(".col-swatch-item").forEach((el) => {
    el.addEventListener("click", () => {
      const col = el.dataset.collection;
      if (state.position === "top") state.top = col;
      else if (state.position === "mid") state.mid = col;
      else state.bottom = col;
      refresh();
    });
  });

  document.getElementById("btn-reset").addEventListener("click", () => {
    state = {
      size: "medium",
      position: "top",
      top: "light",
      mid: "light",
      bottom: "light",
    };
    document
      .querySelectorAll(".col-pill")
      .forEach((b) =>
        b.classList.toggle("active", b.dataset.size === "medium"),
      );
    setPosBtnActive("top");
    btnMid.disabled = false;
    refresh();
  });

  // ── OVERLAY (review step) ─────────────────────────────────────────────────
  const overlay = document.getElementById("col-overlay");
  const errorEl = document.getElementById("col-form-error");
  const submitBtn = document.getElementById("btn-submit");
  const LAYER_LABELS = { top: "Top", mid: "Middle", bottom: "Bottom" };
  const ROOT =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
    "/";

  function showError(message) {
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  function clearError() {
    if (!errorEl) return;
    errorEl.textContent = "";
    errorEl.hidden = true;
  }

  function openOverlay() {
    updateSummary();
    clearError();
    const layers = currentLayers();
    const missing = layers.filter((l) => !l.variant);
    const soldOut = layers.filter((l) => l.variant && !l.variant.available);
    submitBtn.disabled = missing.length > 0;
    if (missing.length > 0) {
      showError(
        "This combination is not available to order online yet. Please choose another scent or size.",
      );
      console.warn(
        "[Circle of Light] No layer product for: " +
          missing
            .map((l) => l.scent + "/" + state.size + "/" + l.layer)
            .join(", ") +
          ". Check each one is in the " +
          state.size +
          " layer collection and has its " +
          "custom.candle_scent and custom.candle_layer metafields set.",
      );
    } else if (soldOut.length > 0) {
      showError("Some layers of this candle are currently out of stock.");
    }
    overlay.classList.add("open");
  }

  function closeOverlay() {
    overlay.classList.remove("open");
  }

  // ── ADD TO SHOPIFY CART ───────────────────────────────────────────────────
  // Each layer is its own line item; "_bundle_id" ties them into one candle. The
  // leading underscore matters twice over: snippets/line-item.liquid skips properties
  // whose key starts with "_", so the plumbing stays invisible in the cart, and the
  // cart drawer reads it to render the set as a single candle and to keep quantity
  // changes and removal applied to every layer at once.
  function buildCartItems() {
    const bundleId = generateBundleId();
    const layers = currentLayers();
    const description = layers
      .map((l) => LAYER_LABELS[l.layer] + ": " + capitalize(l.scent))
      .join(", ");

    return layers.map((entry) => ({
      id: entry.variant.id,
      quantity: 1,
      properties: {
        _bundle_id: bundleId,
        _bundle_size: state.size,
        _bundle_layer: entry.layer,
        _bundle_scent: entry.scent,
        _bundle_description: description,
        Candle: capitalize(state.size) + " Circle of Light",
        Layer: LAYER_LABELS[entry.layer] + ": " + capitalize(entry.scent),
      },
    }));
  }

  async function addToCart() {
    if (currentLayers().some((l) => !l.variant)) {
      showError("This combination is not available to order online yet.");
      return;
    }

    clearError();
    const originalLabel = submitBtn.textContent;
    submitBtn.disabled = true;
    submitBtn.textContent = "Adding…";

    try {
      // Ask the theme which sections want re-rendering, so the cart drawer refreshes
      // through its own machinery instead of us reaching into its markup.
      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:prepare-bundled-sections", {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        }),
      );

      const response = await fetch(ROOT + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          items: buildCartItems(),
          sections: sectionsToBundle.join(","),
        }),
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          payload.description ||
            payload.message ||
            "Cart error " + response.status,
        );
      }

      const cart = await (await fetch(ROOT + "cart.js")).json();
      cart.sections = payload.sections;

      closeOverlay();

      document.documentElement.dispatchEvent(
        new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            cart: cart,
            baseEvent: "variant:add",
            onSuccessDo: "force_open_drawer",
          },
        }),
      );
      document.dispatchEvent(new CustomEvent("cart:refresh"));
    } catch (err) {
      console.error("[Circle of Light] Add to cart failed:", err);
      showError(
        err.message ||
          "Could not add this candle to your cart. Please try again.",
      );
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = originalLabel;
    }
  }

  document
    .getElementById("btn-preorder")
    .addEventListener("click", openOverlay);
  document.getElementById("btn-close").addEventListener("click", closeOverlay);
  document.getElementById("btn-cancel").addEventListener("click", closeOverlay);
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) closeOverlay();
  });
  submitBtn.addEventListener("click", addToCart);

  function preloadDefault() {
    [
      "light-top.png",
      "light-mid.png",
      "light-bottom.png",
      "bottom-shadow.png",
    ].forEach((name) => {
      const img = new Image();
      img.src = CDN + name;
    });
  }

  function preloadAll() {
    const collections = [
      "light",
      "water",
      "bloom",
      "endless",
      "psyche",
      "reflections",
      "feathers",
      "python",
      "fur",
      "gold",
    ];
    const layers = ["top", "mid", "bottom", "top-small", "bottom-small"];
    collections.forEach((col) => {
      layers.forEach((layer) => {
        const img = new Image();
        img.src = `${CDN}${col}-${layer}.png`;
      });
    });
    ["bottom-shadow.png", "bottom-shadow-small.png"].forEach((name) => {
      const img = new Image();
      img.src = CDN + name;
    });
  }

  preloadDefault();
  if ("requestIdleCallback" in window) {
    requestIdleCallback(preloadAll);
  } else {
    setTimeout(preloadAll, 2000);
  }

  refresh();

  // ── STAGE SCROLL BEHAVIOUR ────────────────────────────────────────────────
  const stageEl = document.querySelector(".col-stage");
  const configuratorEl = document.getElementById("col-configurator");

  function updateStageScroll() {
    if (window.innerWidth <= 900) {
      stageEl.style.transform = "";
      return;
    }
    const overshoot = Math.max(
      0,
      window.innerHeight - configuratorEl.getBoundingClientRect().bottom,
    );
    stageEl.style.transform =
      overshoot > 0 ? `translateY(-${overshoot}px)` : "";
  }

  window.addEventListener("scroll", updateStageScroll, { passive: true });
  window.addEventListener("resize", updateStageScroll);
  // ── END STAGE SCROLL BEHAVIOUR ────────────────────────────────────────────

  // ── SAVED COMBINATIONS ───────────────────────────────────────────────────
  const SLOTS_KEY = "cadar-col-slots";

  function loadSlots() {
    try {
      const raw = localStorage.getItem(SLOTS_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return [parsed[0] || null, parsed[1] || null, parsed[2] || null];
    } catch (e) {
      return [null, null, null];
    }
  }

  function saveSlots(slots) {
    localStorage.setItem(SLOTS_KEY, JSON.stringify(slots));
  }

  function closeAllPopovers() {
    document
      .querySelectorAll(".col-slot-popover.open")
      .forEach((p) => p.classList.remove("open"));
  }

  function makeSliceSVG(slot, uid) {
    const s = 34,
      n = slot.size === "small" ? 2 : 3;
    const layers =
      slot.size === "small" ? ["top", "bottom"] : ["top", "mid", "bottom"];
    const bandH = s / n;
    const rects = layers
      .map(
        (layer, i) =>
          `<rect x="0" y="${(i * bandH).toFixed(2)}" width="${s}" height="${bandH.toFixed(2)}" fill="${SWATCH_COLORS[slot[layer]] || "#ccc"}"/>`,
      )
      .join("");
    const sep = layers
      .slice(1)
      .map(
        (_, i) =>
          `<line x1="0" y1="${((i + 1) * bandH).toFixed(2)}" x2="${s}" y2="${((i + 1) * bandH).toFixed(2)}" stroke="#fff" stroke-width="1"/>`,
      )
      .join("");
    return `<svg width="${s}" height="${s}" viewBox="0 0 ${s} ${s}"><defs><clipPath id="cc${uid}"><circle cx="17" cy="17" r="16"/></clipPath></defs><g clip-path="url(#cc${uid})">${rects}${sep}</g></svg>`;
  }

  function renderSlots() {
    const slots = loadSlots();
    const container = document.getElementById("col-slots");
    if (!container) return;
    container.innerHTML = "";

    slots.forEach((slot, i) => {
      const btn = document.createElement("button");
      btn.className = "col-slot" + (slot ? " col-slot--filled" : "");

      if (slot) {
        const svgWrap = document.createElement("div");
        svgWrap.innerHTML = makeSliceSVG(slot, i);
        const sizeLabel = document.createElement("span");
        sizeLabel.className = "col-slot-sizelabel";
        sizeLabel.textContent = slot.size.charAt(0).toUpperCase();
        btn.appendChild(svgWrap);
        btn.appendChild(sizeLabel);

        const popover = document.createElement("div");
        popover.className = "col-slot-popover";

        [
          ["Load", false],
          ["Replace", false],
          ["Remove", true],
        ].forEach(([label, isDanger]) => {
          const pbtn = document.createElement("button");
          pbtn.className = "col-slot-popover-btn" + (isDanger ? " danger" : "");
          pbtn.textContent = label;
          pbtn.addEventListener("click", (e) => {
            e.stopPropagation();
            const current = loadSlots();
            if (label === "Load") {
              const saved = current[i];
              state.size = saved.size;
              state.top = saved.top;
              state.mid = saved.mid;
              state.bottom = saved.bottom;
              if (state.size === "small" && state.position === "mid")
                state.position = "top";
              document
                .querySelectorAll(".col-pill")
                .forEach((b) =>
                  b.classList.toggle("active", b.dataset.size === state.size),
                );
              setPosBtnActive(state.position);
              btnMid.disabled = state.size === "small";
              refresh();
            } else if (label === "Replace") {
              current[i] = {
                size: state.size,
                top: state.top,
                mid: state.mid,
                bottom: state.bottom,
              };
              saveSlots(current);
            } else if (label === "Remove") {
              current[i] = null;
              saveSlots(current);
            }
            closeAllPopovers();
            renderSlots();
          });
          popover.appendChild(pbtn);
        });

        btn.appendChild(popover);
        btn.addEventListener("click", (e) => {
          e.stopPropagation();
          const isOpen = popover.classList.contains("open");
          closeAllPopovers();
          if (!isOpen) popover.classList.add("open");
        });
      } else {
        const plus = document.createElement("span");
        plus.className = "col-slot-plus";
        plus.textContent = "+";
        btn.appendChild(plus);
        btn.addEventListener("click", () => {
          const current = loadSlots();
          current[i] = {
            size: state.size,
            top: state.top,
            mid: state.mid,
            bottom: state.bottom,
          };
          saveSlots(current);
          renderSlots();
        });
      }

      container.appendChild(btn);
    });
  }

  document.addEventListener("click", closeAllPopovers);

  document.getElementById("btn-reset-slots").addEventListener("click", () => {
    if (!confirm("Remove all saved combinations?")) return;
    saveSlots([null, null, null]);
    renderSlots();
  });

  renderSlots();
  // ── END SAVED COMBINATIONS ───────────────────────────────────────────────
})();
